import {
  EMOJI_ARTWORK_CELL_SIZE,
  EMOJI_ARTWORK_SHEET_URL,
  EMOJI_ARTWORK_SOURCE_SIZE,
  resolveEmojiRendering,
  resolveRenderableEmojiArtwork,
} from '@/design-system/components/EmojiPicker/emojiArtwork.js';
import {
  DEFAULT_MESSENGER_V2_IMAGE_EDIT_BRUSH_COLOR,
  DEFAULT_MESSENGER_V2_IMAGE_EDIT_COLOR,
  MESSENGER_V2_IMAGE_TEXT_MAX_WIDTH,
  hasMessengerV2ImageEdits,
  resolveMessengerV2ImageEditDocument,
  resolveMessengerV2ImageLayerPixelSize,
} from './imageEditDocument.js';

const MAX_OUTPUT_EDGE = 2048;
const SUPPORTED_OUTPUT_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const imageCache = new Map();

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, Number(value) || 0));

const createThemeColorResolver = () => {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    !document.body ||
    typeof window.getComputedStyle !== 'function'
  ) {
    return {
      resolve: (color) => color,
      dispose: () => {},
    };
  }

  const probe = document.createElement('span');
  probe.hidden = true;
  probe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(probe);
  return {
    resolve: (color) => {
      const value = String(color || '').trim();
      if (!value.startsWith('var(')) return value;
      probe.style.color = value;
      return window.getComputedStyle(probe).color || value;
    },
    dispose: () => probe.remove(),
  };
};

const loadImage = (source) => {
  const key = String(source || '');
  if (!key) return Promise.reject(new Error('Image source is required'));
  const cacheable = !key.startsWith('blob:');
  if (cacheable && imageCache.has(key)) return imageCache.get(key);

  const promise = new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load image'));
    image.src = key;
  }).catch((error) => {
    if (cacheable) imageCache.delete(key);
    throw error;
  });
  if (cacheable) imageCache.set(key, promise);
  return promise;
};

const createCanvas = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
};

const resolveRotatedSize = (width, height, rotation) => {
  const radians = (rotation * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(radians) * width) + Math.abs(Math.sin(radians) * height),
    height: Math.abs(Math.sin(radians) * width) + Math.abs(Math.cos(radians) * height),
  };
};

const drawRotatedSource = (image, rotation) => {
  const size = resolveRotatedSize(
    image.naturalWidth || image.width,
    image.naturalHeight || image.height,
    rotation,
  );
  const canvas = createCanvas(size.width, size.height);
  const context = canvas.getContext('2d');
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((rotation * Math.PI) / 180);
  context.drawImage(
    image,
    -(image.naturalWidth || image.width) / 2,
    -(image.naturalHeight || image.height) / 2,
  );
  return canvas;
};

const splitTextSegment = (context, segment, maximumWidth) => {
  if (!segment) return [''];
  const lines = [];
  let current = '';
  for (const character of segment) {
    const candidate = `${current}${character}`;
    if (current && context.measureText(candidate).width > maximumWidth) {
      lines.push(current);
      current = character;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const wrapText = (context, text, maximumWidth) =>
  String(text)
    .split('\n')
    .flatMap((paragraph) => {
      if (!paragraph) return [''];
      const lines = [];
      let current = '';
      for (const word of paragraph.split(/\s+/)) {
        const candidate = current ? `${current} ${word}` : word;
        if (context.measureText(candidate).width <= maximumWidth) {
          current = candidate;
          continue;
        }
        if (current) lines.push(current);
        if (context.measureText(word).width <= maximumWidth) {
          current = word;
          continue;
        }
        const segments = splitTextSegment(context, word, maximumWidth);
        lines.push(...segments.slice(0, -1));
        current = segments.at(-1) || '';
      }
      if (current) lines.push(current);
      return lines.length ? lines : [''];
    });

const drawTextLayer = (context, layer, width, height, resolveColor) => {
  const fontSize = resolveMessengerV2ImageLayerPixelSize(layer, height);
  const text = String(layer.text || '');
  if (!text) return;
  context.save();
  context.translate(layer.x * width, layer.y * height);
  context.rotate(((Number(layer.rotation) || 0) * Math.PI) / 180);
  context.font = `600 ${fontSize}px Inter, Arial, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const paddingX = fontSize * 0.3;
  const paddingY = fontSize * 0.18;
  const maximumBoxWidth =
    width *
    clamp(
      layer.maxWidth || MESSENGER_V2_IMAGE_TEXT_MAX_WIDTH,
      0.1,
      MESSENGER_V2_IMAGE_TEXT_MAX_WIDTH,
    );
  const lines = wrapText(context, text, Math.max(1, maximumBoxWidth - paddingX * 2));
  const lineHeight = fontSize * 1.1;
  const lineWidths = lines.map((line) => context.measureText(line).width);
  const boxWidth = Math.min(maximumBoxWidth, Math.max(...lineWidths, 0) + paddingX * 2);
  const boxHeight = lines.length * lineHeight + paddingY * 2;
  const visibleBoxHeight = Math.min(boxHeight, height);
  if (layer.background && layer.background !== 'transparent') {
    context.fillStyle = resolveColor(layer.background);
    context.beginPath();
    if (typeof context.roundRect === 'function') {
      context.roundRect(
        -boxWidth / 2,
        -visibleBoxHeight / 2,
        boxWidth,
        visibleBoxHeight,
        fontSize * 0.22,
      );
    } else {
      context.rect(-boxWidth / 2, -visibleBoxHeight / 2, boxWidth, visibleBoxHeight);
    }
    context.fill();
  }
  context.fillStyle = resolveColor(layer.color || DEFAULT_MESSENGER_V2_IMAGE_EDIT_COLOR);
  const firstLineY =
    boxHeight > height
      ? -visibleBoxHeight / 2 + paddingY + lineHeight / 2
      : -((lines.length - 1) * lineHeight) / 2;
  lines.forEach((line, index) => {
    context.fillText(line, 0, firstLineY + index * lineHeight, maximumBoxWidth - paddingX * 2);
  });
  context.restore();
};

const drawEmojiLayer = async (context, layer, width, height) => {
  const size = resolveMessengerV2ImageLayerPixelSize(layer, height);
  const rendering = resolveEmojiRendering(layer.emoji);
  context.save();
  context.translate(layer.x * width, layer.y * height);
  context.rotate(((Number(layer.rotation) || 0) * Math.PI) / 180);
  if (rendering === 'native') {
    context.font = `${size}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Samsung Color Emoji", sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(layer.emoji, 0, 0);
  } else {
    const { position } = resolveRenderableEmojiArtwork(layer.emoji);
    const sheet = await loadImage(EMOJI_ARTWORK_SHEET_URL);
    context.drawImage(
      sheet,
      position.x * EMOJI_ARTWORK_CELL_SIZE + 1,
      position.y * EMOJI_ARTWORK_CELL_SIZE + 1,
      EMOJI_ARTWORK_SOURCE_SIZE,
      EMOJI_ARTWORK_SOURCE_SIZE,
      -size / 2,
      -size / 2,
      size,
      size,
    );
  }
  context.restore();
};

const drawStrokeLayer = (context, layer, width, height, resolveColor) => {
  if (!Array.isArray(layer.points) || layer.points.length < 2) return;
  context.save();
  context.strokeStyle = resolveColor(layer.color || DEFAULT_MESSENGER_V2_IMAGE_EDIT_BRUSH_COLOR);
  context.lineWidth = Math.max(1, layer.width * Math.min(width, height));
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.beginPath();
  layer.points.forEach((point, index) => {
    const x = point.x * width;
    const y = point.y * height;
    if (index === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  });
  context.stroke();
  context.restore();
};

export const renderMessengerV2ImageEdit = async ({
  canvas,
  sourceUrl,
  document: editDocument,
  maxEdge = MAX_OUTPUT_EDGE,
  includeLayers = true,
} = {}) => {
  if (!canvas) throw new Error('Canvas is required');
  const image = await loadImage(sourceUrl);
  const document = resolveMessengerV2ImageEditDocument(editDocument);
  const { crop, layers } = document.present;
  const rotation = Number(crop.rotation) || 0;
  const requiresRotationCanvas = Math.abs(rotation % 360) > 0.001;
  const rotated = requiresRotationCanvas ? drawRotatedSource(image, rotation) : image;
  try {
    const cropX = clamp(crop.area.x / 100, 0, 1) * rotated.width;
    const cropY = clamp(crop.area.y / 100, 0, 1) * rotated.height;
    const cropWidth = Math.max(1, clamp(crop.area.width / 100, 0.001, 1) * rotated.width);
    const cropHeight = Math.max(1, clamp(crop.area.height / 100, 0.001, 1) * rotated.height);
    const outputScale = Math.min(
      1,
      Math.max(1, Number(maxEdge) || MAX_OUTPUT_EDGE) / Math.max(cropWidth, cropHeight),
    );
    canvas.width = Math.max(1, Math.round(cropWidth * outputScale));
    canvas.height = Math.max(1, Math.round(cropHeight * outputScale));
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      rotated,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    if (!includeLayers) return canvas;
    const colorResolver = createThemeColorResolver();
    try {
      for (const layer of layers) {
        if (layer.type === 'stroke') {
          drawStrokeLayer(context, layer, canvas.width, canvas.height, colorResolver.resolve);
        } else if (layer.type === 'text') {
          drawTextLayer(context, layer, canvas.width, canvas.height, colorResolver.resolve);
        } else if (layer.type === 'emoji') {
          await drawEmojiLayer(context, layer, canvas.width, canvas.height);
        }
      }
    } finally {
      colorResolver.dispose();
    }
    return canvas;
  } finally {
    if (requiresRotationCanvas) {
      rotated.width = 1;
      rotated.height = 1;
    }
  }
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not render edited image'))),
      type,
      quality,
    );
  });

const replaceImageExtension = (name, mimeType) => {
  const extension = mimeType === 'image/jpeg' ? 'jpg' : mimeType.split('/')[1] || 'png';
  const baseName = String(name || 'image').replace(/\.[^.]+$/, '') || 'image';
  return `${baseName}-edited.${extension}`;
};

export const materializeMessengerV2ImageEdit = async (draft) => {
  if (!draft?.file || !hasMessengerV2ImageEdits(draft.editDocument)) {
    return draft?.file || null;
  }
  const ownsSourceUrl = !draft.previewUrl;
  const sourceUrl =
    draft.previewUrl ||
    (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
      ? URL.createObjectURL(draft.file)
      : null);
  if (!sourceUrl) throw new Error('Edited image preview is unavailable');
  let canvas = null;
  try {
    canvas = createCanvas(1, 1);
    await renderMessengerV2ImageEdit({
      canvas,
      sourceUrl,
      document: draft.editDocument,
    });
    const outputType = SUPPORTED_OUTPUT_TYPES.has(draft.mimeType) ? draft.mimeType : 'image/png';
    const blob = await canvasToBlob(
      canvas,
      outputType,
      outputType === 'image/jpeg' ? 0.92 : undefined,
    );
    const name = replaceImageExtension(draft.name || draft.file.name, outputType);
    return typeof File === 'function'
      ? new File([blob], name, { type: outputType, lastModified: Date.now() })
      : Object.assign(blob, { name, lastModified: Date.now() });
  } finally {
    if (canvas) {
      canvas.width = 1;
      canvas.height = 1;
    }
    if (ownsSourceUrl) {
      URL.revokeObjectURL?.(sourceUrl);
    }
  }
};

export const materializeMessengerV2ImageEditDrafts = async (
  drafts = [],
  { onProgress = null } = {},
) => {
  const normalizedDrafts = Array.isArray(drafts) ? drafts.filter((draft) => draft?.file) : [];
  const materializedFiles = [];
  onProgress?.({ completed: 0, total: normalizedDrafts.length });

  for (let index = 0; index < normalizedDrafts.length; index += 1) {
    // Keep one decoded source and one export canvas alive at a time. Large mobile photo
    // batches otherwise exceed the renderer's memory budget before an upload can start.
    // eslint-disable-next-line no-await-in-loop
    const file = await materializeMessengerV2ImageEdit(normalizedDrafts[index]);
    materializedFiles.push(file);
    onProgress?.({ completed: index + 1, total: normalizedDrafts.length });
  }

  return materializedFiles;
};
